// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';

/** One job a worker is busy with. */
export type FleetJob = {
  id: string;
  /** course-analysis | flyover | ml. */
  kind: string;
  /** Course name, when the job kind is course-scoped. */
  courseName: string | null;
  /** Which job of the run this is (kind · provider · detector@model) for an analysis job; null otherwise. */
  detail: string | null;
  /** Taken from its queue but not started — the worker's analyser is busy with another job. */
  waiting: boolean;
};

/** One batch-worker server as the fleet page shows it. */
export type FleetWorker = {
  workerId: string;
  host: string;
  version: string | null;
  capabilities: string[];
  /**
   * Every job the worker is busy with right now, oldest first — one per job kind at most,
   * since each kind is consumed on its own queue. Empty when idle. A list, not one job: a
   * machine training a model that also picked up a course analysis used to show "ML job"
   * alone, and the analysis read as never having started.
   */
  currentJobs: FleetJob[];
  lastSeenAt: string;
  online: boolean;
  /** When the worker process started, as it reports it. Null for a worker build that predates it. */
  startedAt: string | null;
  /** linux | windows | macos | unknown | null — null for a worker build that predates this field. */
  os: string | null;
  /** arm64 | x64 | ... . */
  architecture: string | null;
  /** CPUs the container can use (Docker-limit-aware, not necessarily the host's full count). */
  cpuCount: number | null;
  /** Memory the container can use, in bytes (a Docker memory limit if one is set, else the host's total). */
  totalMemoryBytes: number | null;
  /**
   * What this worker's ML training runner trains on — mps | cuda | cpu — or null when it has no
   * runner, could not reach it, or predates the field. Not the worker container's own hardware:
   * that is always a Linux VM with no accelerator, which is why a containerised runner reads cpu.
   */
  mlAccelerator: string | null;
  /**
   * The trained models this worker's analyser can run, by registry version. Every one of them
   * is run against a course after its classical analysis, so this says how many comparison
   * runs the machine contributes — empty means no ML analyser beside it, or a release too old
   * to report them.
   */
  modelVersions: string[];
};

/** All-time totals for one job kind. */
export type FleetKindStatistics = { total: number; succeeded: number; failed: number };

/** Fleet-wide numbers: how many servers, and how the three job kinds have done all-time. */
export type FleetStatistics = {
  workersTotal: number;
  workersOnline: number;
  courseAnalysis: FleetKindStatistics;
  flyover: FleetKindStatistics;
  ml: FleetKindStatistics;
};

/** How many jobs of each kind are waiting for a worker right now. */
export type FleetQueues = {
  courseAnalysisDispatched: number;
  flyoverQueued: number;
  mlQueued: number;
  /** Analysis jobs planned or out on a worker, by job kind (detection, elevation, …). */
  analysisJobsOpen: Record<string, number>;
};

/** One job from any of the three kinds, already merged into one timeline. */
export type FleetActivityItem = {
  kind: 'course-analysis' | 'flyover' | 'ml';
  id: string;
  status: string;
  at: string;
  finishedAt: string | null;
  workerId: string | null;
  summary: string | null;
  error: string | null;
  /** Course analyses: the run this job belongs to — what the steps panel opens. */
  runId: string | null;
  /** Course analyses: which job of the run this is (kind · provider · detector@model). */
  detail: string | null;
};

export type FleetSnapshot = {
  workers: FleetWorker[];
  queues: FleetQueues;
  statistics: FleetStatistics;
  activity: FleetActivityItem[];
};

export type FleetState =
  | { status: 'loading' }
  | { status: 'forbidden' }
  | { status: 'failed' }
  | ({ status: 'ready'; loadedAt: number } & FleetSnapshot);

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return 0;
};

const numOrNull = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const bool = (row: Row, ...keys: string[]): boolean => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'boolean') return value;
  }
  return false;
};

const strArray = (row: Row, ...keys: string[]): string[] => {
  for (const key of keys) {
    const value = row[key];
    if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
};

/**
 * The worker's in-flight jobs. A geodata that predates the list reports one job in the three
 * `currentJob*` fields instead; that job is mapped as a one-element list so the card still
 * shows it.
 */
const jobs = (row: Row): FleetJob[] => {
  const rows = row.currentJobs ?? row.CurrentJobs;
  if (Array.isArray(rows)) {
    return rows
      .map((job) => {
        const r = (job ?? {}) as Row;
        const id = str(r, 'id', 'Id');
        const kind = str(r, 'kind', 'Kind');
        return id && kind
          ? {
              id,
              kind,
              courseName: str(r, 'courseName', 'CourseName'),
              detail: str(r, 'detail', 'Detail'),
              waiting: r.waiting === true || r.Waiting === true,
            }
          : null;
      })
      .filter((job): job is FleetJob => job !== null);
  }

  const id = str(row, 'currentJobId', 'CurrentJobId');
  const kind = str(row, 'currentJobKind', 'CurrentJobKind');
  return id && kind
    ? [{ id, kind, courseName: str(row, 'currentJobCourseName', 'CurrentJobCourseName'), detail: null, waiting: false }]
    : [];
};

const isFleetKind = (value: string | null): value is FleetActivityItem['kind'] =>
  value === 'course-analysis' || value === 'flyover' || value === 'ml';

/** Maps the BFF's `/api/geodata/batch/fleet` body — pulled out for direct unit testing. */
export const mapFleetResponse = (body: unknown): FleetSnapshot => {
  const root = (body ?? {}) as Row;

  const workerRows = (root.workers ?? root.Workers) as Row[] | undefined;
  const workers: FleetWorker[] = Array.isArray(workerRows)
    ? workerRows
        .map((row) => ({
          workerId: str(row, 'workerId', 'WorkerId') ?? '',
          host: str(row, 'host', 'Host') ?? '',
          version: str(row, 'version', 'Version'),
          capabilities: strArray(row, 'capabilities', 'Capabilities'),
          currentJobs: jobs(row),
          lastSeenAt: str(row, 'lastSeenAt', 'LastSeenAt') ?? '',
          online: bool(row, 'online', 'Online'),
          startedAt: str(row, 'startedAt', 'StartedAt'),
          os: str(row, 'os', 'Os'),
          architecture: str(row, 'architecture', 'Architecture'),
          cpuCount: numOrNull(row, 'cpuCount', 'CpuCount'),
          totalMemoryBytes: numOrNull(row, 'totalMemoryBytes', 'TotalMemoryBytes'),
          mlAccelerator: str(row, 'mlAccelerator', 'MlAccelerator'),
          modelVersions: strArray(row, 'modelVersions', 'ModelVersions'),
        }))
        .filter((worker) => worker.workerId.length > 0)
    : [];

  const queueRow = (root.queues ?? root.Queues ?? {}) as Row;
  const queues: FleetQueues = {
    courseAnalysisDispatched: num(queueRow, 'courseAnalysisDispatched', 'CourseAnalysisDispatched'),
    flyoverQueued: num(queueRow, 'flyoverQueued', 'FlyoverQueued'),
    mlQueued: num(queueRow, 'mlQueued', 'MlQueued'),
    analysisJobsOpen: Object.fromEntries(
      Object.entries((queueRow.analysisJobsOpen ?? queueRow.AnalysisJobsOpen ?? {}) as Record<string, unknown>)
        .filter(([, count]) => typeof count === 'number' && Number.isFinite(count))
        .map(([kind, count]) => [kind, count as number]),
    ),
  };

  const mapKindStatistics = (row: Row): FleetKindStatistics => ({
    total: num(row, 'total', 'Total'),
    succeeded: num(row, 'succeeded', 'Succeeded'),
    failed: num(row, 'failed', 'Failed'),
  });
  const statsRow = (root.statistics ?? root.Statistics ?? {}) as Row;
  const statistics: FleetStatistics = {
    workersTotal: num(statsRow, 'workersTotal', 'WorkersTotal'),
    workersOnline: num(statsRow, 'workersOnline', 'WorkersOnline'),
    courseAnalysis: mapKindStatistics((statsRow.courseAnalysis ?? statsRow.CourseAnalysis ?? {}) as Row),
    flyover: mapKindStatistics((statsRow.flyover ?? statsRow.Flyover ?? {}) as Row),
    ml: mapKindStatistics((statsRow.ml ?? statsRow.Ml ?? {}) as Row),
  };

  const activityRows = (root.recentActivity ?? root.RecentActivity) as Row[] | undefined;
  const activity: FleetActivityItem[] = Array.isArray(activityRows)
    ? activityRows
        .map((row) => {
          const kind = str(row, 'kind', 'Kind');
          const id = str(row, 'id', 'Id');
          const at = str(row, 'at', 'At');
          if (!isFleetKind(kind) || !id || !at) return null;
          return {
            kind,
            id,
            status: str(row, 'status', 'Status') ?? '',
            at,
            finishedAt: str(row, 'finishedAt', 'FinishedAt'),
            workerId: str(row, 'workerId', 'WorkerId'),
            summary: str(row, 'summary', 'Summary'),
            error: str(row, 'error', 'Error'),
            runId: str(row, 'runId', 'RunId'),
            detail: str(row, 'detail', 'Detail'),
          };
        })
        .filter((item): item is FleetActivityItem => item !== null)
    : [];

  return { workers, queues, statistics, activity };
};

/**
 * Polls the batch-worker fleet page's data. Kept to a plain interval rather than a
 * WebSocket subscription: this is a read-only operational view a developer opens
 * occasionally, not a live control surface, and geodata already answers the same shape to
 * the per-course/per-job panels from a query with no push side to reuse.
 */
export function useBatchFleet(
  apiBaseUrl: string,
  token: string | undefined,
  enabled: boolean,
  pollMs = 15000
): { state: FleetState; refresh: () => void } {
  const [state, setState] = useState<FleetState>({ status: 'loading' });
  const reloadKeyRef = useRef(0);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => {
    reloadKeyRef.current += 1;
    setReloadKey(reloadKeyRef.current);
  }, []);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/geodata/batch/fleet`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const body = await response.json();
        if (cancelled) return;
        setState({ status: 'ready', loadedAt: Date.now(), ...mapFleetResponse(body) });
      } catch {
        if (!cancelled) {
          setState({ status: 'failed' });
        }
      } finally {
        if (!cancelled) {
          timer = setTimeout(load, pollMs);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [apiBaseUrl, token, enabled, pollMs, reloadKey]);

  return { state, refresh };
}
